import React, { useState, useEffect } from "react";
import {
  Modal, Button, Row, Col, Select, Input, DatePicker, Upload, Tag,
  Steps, Typography, Table, Alert, Spin, Checkbox, message,
} from "antd";
import { UploadOutlined, CheckCircleTwoTone } from "@ant-design/icons";
import moment from "moment";
import HistoricalOrderBulkUpload from "../HistoricalOrderBulkUpload";
import useHistoricalOrderMasterData from "../HistoricalOrderBulkUpload/useHistoricalOrderMasterData";
import { createHistoricalOrder } from "../../../../../../../services/apis";
import buildHistoricalOrderFormData from "../HistoricalOrderBulkUpload/buildHistoricalOrderFormData";
import { SAMPLE_PHOTO_FIELDS, FIELD_DEF_BY_FIELD } from "../HistoricalOrderBulkUpload/fieldDefs";
import { FIXED_OPTIONS, STAGE_DATE_FIELDS } from "./constants";

const { Option } = Select;
const { Text, Title } = Typography;
const { Step } = Steps;

const STEP = Object.freeze({
  CLIENT_PROJECT: 0, ORDER_DETAILS: 1, SAMPLE_PURCHASE: 2,
  LAB_LOGISTICS: 3, DATES: 4, REPORT_COMPLIANCE: 5, REVIEW: 6,
});

const TARGET_END_STATUS = Object.freeze({ REPORT_UPLOADED: "Report Uploaded", COMPLETED: "Completed" });

// Mirrors be/services/historicalOrderCreate.service.js's REQUIRED_FIELDS /
// REQUIRED_NESTED_FIELDS exactly, which in turn mirror the real sheet's
// authoritative REQUIRED_HEADERS (see /google-sheets/historical-orders/
// Code.gs) — plus the conditional completedDate, which that service checks
// separately as a chronology rule. Fields not listed here (sampler,
// siteAddress, batchId, notes, seal/QR numbers, sample registration number,
// tracking id, metadata file) really are optional server-side too. Every
// stage date, though, IS required both here and server-side.
const REQUIRED_FIELDS_BY_STEP = {
  [STEP.CLIENT_PROJECT]: [["company", "Company"], ["projectCode", "Project Code"]],
  [STEP.ORDER_DETAILS]: [
    ["agency", "Agency"], ["commodity", "Commodity"], ["package", "Package"],
    ["sampleVariant", "Sample Variant"], ["sampleType", "Sample Type"], ["sector", "Sector"],
    ["state", "State"], ["district", "District"], ["supplier", "Supplier"],
    ["quantity", "Quantity"], ["quantityUnit", "Quantity Unit"],
  ],
  [STEP.SAMPLE_PURCHASE]: [
    ["nameOfShop", "Name of Shop"], ["addressOfShop", "Address of Shop"],
    ["sampleWeight", "Sample Weight (g)"], ["sampleCost", "Sample Cost (INR)"], ["typeOfShop", "Type of Shop"],
    ["samplePackedIn", "Sample Packed In"], ["fssaiLogoPresent", "FSSAI Logo Present?"],
    ["vegSymbolPresent", "Veg Symbol Present?"],
    ["samplePhysicalCondition", "Sample Physical Condition"], ["conditionOfSeal", "Condition of Seal"],
  ],
  [STEP.LAB_LOGISTICS]: [
    ["assignedLab", "Assigned Lab"], ["assignedLogistics", "Assigned Logistics"],
  ],
  // Every date field lives here, in true chronological order — see
  // DATE_CHAIN_KEYS below for the interdependent bounds between them. Every
  // stage is required now (not just pendingDate/reportUploadedDate) — in
  // grouped mode (the "Use separate stage dates" checkbox off) the other
  // members of each DATE_GROUPS pair are always kept in sync with their
  // representative field by `update()`, so requiring all of them here never
  // adds friction beyond what requiring the representative already does.
  [STEP.DATES]: [
    ["dateOfPurchase", "Date of Purchase"], ...STAGE_DATE_FIELDS,
    ["expectedDateOfCompletion", "Expected Date of Completion"],
    ["targetEndStatus", "Target End Status"],
  ],
  [STEP.REPORT_COMPLIANCE]: [
    ["reportFile", "Report File"], ["reportMetadataFile", "Report Metadata File"],
    ["overallSafetyCompliant", "Overall Safety Compliant"], ["overallQualityCompliant", "Overall Quality Compliant"],
    ["overallLabellingCompliant", "Overall Labelling Compliant"], ["overallSampleClassification", "Overall Sample Classification"],
  ],
  [STEP.REVIEW]: [],
};

const INITIAL_DATA = {
  company: "", projectCode: "", agency: "", commodity: "", package: "",
  sampleVariant: "", sampleType: "", sector: "", state: "", district: "",
  sampler: "", siteAddress: "", batchId: "", supplier: "", quantity: "",
  quantityUnit: "", notes: "",
  nameOfShop: "", addressOfShop: "", dateOfPurchase: null, sampleWeight: "",
  sampleCost: "", typeOfShop: "", sampleSealNo: "", qrCodeId: "",
  samplePackedIn: "", fssaiLogoPresent: "", vegSymbolPresent: "",
  assignedLab: "", assignedLogistics: "", trackingId: "",
  samplePhysicalCondition: "", conditionOfSeal: "", sampleRegistrationNumber: "",
  expectedDateOfCompletion: null,
  pendingDate: null, bookedDate: null, inReviewDate: null, readyForPickupDate: null,
  outForPickupDate: null, deliveredToLabDate: null, receivedAtLabDate: null,
  underTestingDate: null, reportUploadedDate: null,
  targetEndStatus: "", completedDate: null,
  reportFile: null, reportMetadataFile: null,
  overallSafetyCompliant: "", overallQualityCompliant: "",
  overallLabellingCompliant: "", overallSampleClassification: "",
  // Every optional single-photo field the bulk flow also supports (see
  // HistoricalOrderBulkUpload/fieldDefs.js's SAMPLE_PHOTO_FIELDS) — kept in
  // sync by deriving the keys from there rather than listing them again.
  ...SAMPLE_PHOTO_FIELDS.reduce((acc, field) => { acc[field] = null; return acc; }, {}),
};

function Field({ label, required, children }) {
  return (
    <Col span={12} style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 4 }}>
        <Text strong style={{ fontSize: 13 }}>{label}</Text>
        {required && <Text type="danger" style={{ marginLeft: 4 }}>*</Text>}
      </div>
      {children}
    </Col>
  );
}

const errorStyle = (hasError) => (hasError ? { border: "1px solid #ff4d4f", borderRadius: 4 } : undefined);

// Accepts either plain strings (fixed enum lists) or {id, name} records
// (real master-data reference lists) — value is always what gets stored.
function FieldSelect({ value, options, onChange, disabled, hasError, placeholder }) {
  const normalized = options.map((opt) => (typeof opt === "string" ? { value: opt, label: opt } : { value: opt.id, label: opt.name }));
  return (
    <Select
      value={value || undefined}
      onChange={onChange}
      disabled={disabled}
      allowClear
      showSearch
      optionFilterProp="children"
      style={{ width: "100%", ...errorStyle(hasError) }}
      placeholder={placeholder}
    >
      {normalized.map((opt) => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
    </Select>
  );
}

function FieldDate({ value, onChange, hasError, placeholder, disabledDate, disabled }) {
  return (
    <DatePicker
      value={value ? moment(value) : null}
      // Normalized to midnight, not the wall-clock time the picker was
      // clicked at: this form only ever collects a calendar day (no
      // showTime), but two same-day fields picked minutes apart would
      // otherwise carry different embedded timestamps. The FE's own
      // chronology checks (chainDisabledDate/validateTimeline) compare at
      // day granularity and treat that as "same day, fine" — the backend's
      // chronology check (historicalOrderCreate.service.js) does not
      // day-truncate, so it would reject same-day stages submitted with a
      // real time-of-day as out of order at the millisecond level.
      onChange={(d) => onChange(d ? d.startOf("day").toISOString() : null)}
      disabledDate={disabledDate}
      disabled={disabled}
      style={{ width: "100%", ...errorStyle(hasError) }}
      placeholder={placeholder}
    />
  );
}

function FieldFileUpload({ file, onSelect, hasError }) {
  return (
    <div>
      {file && <Tag style={{ marginBottom: 6 }}>{file.name}</Tag>}
      <Upload showUploadList={false} beforeUpload={(f) => { onSelect(f); return false; }}>
        <Button size="small" danger={hasError} icon={<UploadOutlined />}>{file ? "Replace" : "Upload"}</Button>
      </Upload>
    </div>
  );
}

// Every date field that describes the order's real historical timeline, in
// true chronological order — Target End Status/Completed Date aren't dates
// themselves but Completed Date (last) only ever needs to be >= Report
// Uploaded Date, so it's included as the terminal entry. Each field's
// DatePicker disables anything before the nearest already-filled EARLIER
// entry and anything after the nearest already-filled LATER entry (skipping
// blanks) — the same rule historicalOrderCreate.service.js already enforces
// server-side, just applied live so an out-of-order pick is never possible
// in the first place, not just rejected after the fact.
const DATE_CHAIN_KEYS = [
  "dateOfPurchase", "pendingDate", "bookedDate", "inReviewDate", "readyForPickupDate",
  "outForPickupDate", "deliveredToLabDate", "receivedAtLabDate", "underTestingDate",
  "reportUploadedDate", "completedDate",
];

// Expected Date of Completion isn't part of that strict chain — it's a
// forward-looking target set once the sample reaches the lab, so it only
// gets a LOWER bound (the nearest already-filled date at/behind Under
// Testing); no upper bound, since a real target can legitimately be missed
// by the time the report is actually uploaded.
const EXPECTED_COMPLETION_ANCHOR_KEYS = [
  "underTestingDate", "receivedAtLabDate", "deliveredToLabDate", "outForPickupDate",
  "readyForPickupDate", "inReviewDate", "bookedDate", "pendingDate", "dateOfPurchase",
];

function nearestFilled(data, keys, fromIndex, direction) {
  for (let i = fromIndex + direction; i >= 0 && i < keys.length; i += direction) {
    if (data[keys[i]]) return data[keys[i]];
  }
  return null;
}

// This form only ever enters orders that have already happened, so NO date
// on it can be in the future — every stage, not just the terminal one.
// (Capping only Report Uploaded/Completed Date and relying on the
// neighbor-bound below to propagate that backwards was the earlier
// approach here, and it was wrong: that only actually constrains earlier
// dates once a LATER one is filled in. Filling the form in its natural
// front-to-back order never touches a later date first, so nothing stopped
// Booked/In Review/Ready for Pickup/etc. from drifting arbitrarily far into
// the future one field at a time — right up until Report Uploaded Date,
// which WAS capped at today, by which point the chain is already
// self-contradicting: a "today-or-earlier" stage sitting before "future"
// stages the picker had already accepted. Capping every stage independently
// closes that regardless of fill order.)
//
// Day-granularity only — these are plain dates (no time-of-day picker), so
// comparisons truncate both sides to the calendar day before comparing.
const DATE_GROUPS = [
  ["pendingDate", "bookedDate", "inReviewDate"],
  ["readyForPickupDate", "outForPickupDate", "deliveredToLabDate"],
];

function chainDisabledDate(data, key, grouped = false) {
  const index = DATE_CHAIN_KEYS.indexOf(key);
  const min = nearestFilled(data, DATE_CHAIN_KEYS, index, -1);
  const group = grouped && DATE_GROUPS.find((keys) => keys.includes(key));
  const endIndex = group ? DATE_CHAIN_KEYS.indexOf(group[group.length - 1]) : index;
  let max = nearestFilled(data, DATE_CHAIN_KEYS, endIndex, 1);
  const today = moment();
  if (!max || today.isBefore(moment(max), "day")) max = today;
  return (current) => {
    if (!current) return false;
    if (min && current.isBefore(moment(min), "day")) return true;
    if (max && current.isAfter(moment(max), "day")) return true;
    return false;
  };
}

// Expected Date of Completion is a target, not a chain stage — its lower
// bound is whichever stage anchors it, never a later stage's date. But it's
// still a value being recorded about an order that's already fully in the
// past (this form doesn't track live orders), so — like every stage above —
// it can't be later than today either, even though it has no upper bound
// relative to its own anchor.
function expectedCompletionDisabledDate(data) {
  const min = EXPECTED_COMPLETION_ANCHOR_KEYS.map((k) => data[k]).find(Boolean) || null;
  const today = moment();
  return (current) => {
    if (!current) return false;
    if (min && current.isBefore(moment(min), "day")) return true;
    if (current.isAfter(today, "day")) return true;
    return false;
  };
}

// Belt-and-suspenders alongside FieldDate's own onChange normalization: any
// stage date already sitting in state from before that fix (e.g. picked in a
// modal session that started before this code shipped, or carried across a
// hot-reload) would still embed a real wall-clock time. Re-normalizing every
// date to midnight right before submit — not just at the moment it's picked
// — guarantees the backend's non-day-truncated chronology check (which only
// rejects on a strict `<`, never `<=`) always sees identical instants for
// same-calendar-day stages, regardless of when/how each value was set.
function normalizeDatesForSubmit(data) {
  const next = { ...data };
  [...DATE_CHAIN_KEYS, "expectedDateOfCompletion"].forEach((key) => {
    if (next[key]) next[key] = moment(next[key]).startOf("day").toISOString();
  });
  return next;
}

function isValidQuantity(value) {
  return value !== null && value !== undefined && String(value).trim() !== "" &&
    Number.isFinite(Number(value)) && Number(value) >= 0;
}

function validateTimeline(data) {
  const errors = [];
  const labels = Object.fromEntries([
    ["dateOfPurchase", "Date of Purchase"], ...STAGE_DATE_FIELDS,
    ["completedDate", "Completed Date"], ["expectedDateOfCompletion", "Expected Date of Completion"],
  ]);
  let previous = null;
  [...DATE_CHAIN_KEYS, "expectedDateOfCompletion"].forEach((key) => {
    if (!data[key]) return;
    const date = moment(data[key]);
    if (!date.isValid()) {
      errors.push([key, `${labels[key]} must be a valid date`]);
      return;
    }
    if (date.isAfter(moment(), "day")) errors.push([key, `${labels[key]} cannot be in the future`]);
    if (key === "expectedDateOfCompletion") {
      const anchor = EXPECTED_COMPLETION_ANCHOR_KEYS.find((field) => data[field]);
      if (anchor && date.isBefore(moment(data[anchor]), "day")) {
        errors.push([key, `${labels[key]} cannot be before ${labels[anchor]}`]);
      }
    } else {
      if (previous && date.isBefore(moment(data[previous]), "day")) {
        errors.push([key, `${labels[key]} cannot be before ${labels[previous]}`]);
      }
      previous = key;
    }
  });
  if (data.completedDate && data.targetEndStatus !== TARGET_END_STATUS.COMPLETED) {
    errors.push(["completedDate", "Completed Date requires a Completed end status"]);
  }
  return errors;
}

/**
 * "Add Completed Order" — single historical/backdated order, entered directly
 * through a step-by-step form (no CSV/sheet). Required fields mirror exactly
 * what the real backend enforces (see REQUIRED_FIELDS_BY_STEP below). A red
 * asterisk marks them from the start; the red-outline error state only
 * appears once you've tried to move past a step with one still empty (not
 * eagerly on load), and clears the moment it's filled in. Everything not
 * listed as required (sampler, purchase details, seal/QR numbers, compliance,
 * metadata file, ...) really is optional server-side too.
 *
 * Real end-to-end: Company/Project/Agency/Commodity/Package/Lab/Logistics
 * come from GET /v1/historical-orders/master-data, and Submit really calls
 * POST /v1/historical-orders/create (be/services/historicalOrderCreate.service.js)
 * — a real Order + SampleDetails + LabSampleReceivedDetails + LabResponse get
 * written with the historical dates given, the report/metadata files are
 * really uploaded to Azure Blob (labx-dev), and real Gemini parsing is
 * triggered. No simulation.
 */
export default function AddCompletedOrder({ visible, onClose }) {
  const { data: masterData, loading: masterDataLoading, error: masterDataError, refresh: refreshMasterData } = useHistoricalOrderMasterData();

  const [step, setStep] = useState(STEP.CLIENT_PROJECT);
  const [furthestStep, setFurthestStep] = useState(STEP.CLIENT_PROJECT);
  // Which steps the user has already tried to leave with a required field
  // still empty — the red outline only shows for a field once its step is in
  // here, not from the moment the step is first shown.
  const [attemptedSteps, setAttemptedSteps] = useState(() => new Set());
  const [data, setData] = useState(INITIAL_DATA);
  const [separateStageDates, setSeparateStageDates] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);

  const update = (field, value) => setData((prev) => {
    const next = { ...prev, [field]: value };
    const group = !separateStageDates && DATE_GROUPS.find((keys) => keys.includes(field));
    if (group) group.forEach((key) => { next[key] = value; });
    // A date field just moved later than an already-filled later date (or
    // Expected Date of Completion's anchor) invalidates that later value —
    // it was valid when it was picked, just not anymore. Clear it forward
    // rather than leave a stale, now-inconsistent value sitting in the form.
    if (value && DATE_CHAIN_KEYS.includes(field)) {
      const index = DATE_CHAIN_KEYS.indexOf(field);
      let clearedCount = 0;
      for (let i = index + 1; i < DATE_CHAIN_KEYS.length; i++) {
        const laterKey = DATE_CHAIN_KEYS[i];
        if (next[laterKey] && moment(next[laterKey]).isBefore(moment(value), "day")) {
          next[laterKey] = null;
          clearedCount += 1;
        }
      }
      if (next.expectedDateOfCompletion && EXPECTED_COMPLETION_ANCHOR_KEYS.includes(field) &&
          moment(next.expectedDateOfCompletion).isBefore(moment(value), "day")) {
        next.expectedDateOfCompletion = null;
        clearedCount += 1;
      }
      if (clearedCount) {
        message.warning(`Cleared ${clearedCount} date${clearedCount > 1 ? "s" : ""} that were now out of order — please re-enter them.`);
      }
    }
    return next;
  });

  const updateWithReset = (field, value, resetFields) => setData((prev) => {
    const next = { ...prev, [field]: value };
    resetFields.forEach((f) => { next[f] = ""; });
    return next;
  });

  const closeAndReset = () => {
    onClose?.();
    setStep(STEP.CLIENT_PROJECT);
    setFurthestStep(STEP.CLIENT_PROJECT);
    setAttemptedSteps(new Set());
    setData(INITIAL_DATA);
    setSeparateStageDates(false);
    setEmail("");
    setSubmitting(false);
    setSubmitError(null);
    setCreatedOrder(null);
  };

  // Guards against accidental loss of work: if the user has entered any values,
  // uploaded files, or moved beyond the initial step, require confirmation.
  const isFormDirty = Boolean(
    data.company ||
    data.projectCode ||
    furthestStep !== STEP.CLIENT_PROJECT ||
    data.reportFile ||
    data.reportMetadataFile ||
    Object.keys(data).some((k) => data[k] !== INITIAL_DATA[k] && data[k] !== null && data[k] !== "")
  );
  const hasUnsavedData = isFormDirty && !createdOrder;

  const confirmCloseIfNeeded = () => {
    if (!hasUnsavedData) {
      closeAndReset();
      return;
    }
    Modal.confirm({
      title: "Discard this order?",
      content: "Everything you've filled in on this form will be cleared and this can't be undone.",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: closeAndReset,
    });
  };

  const navigationGuardActive = visible && !bulkModalVisible;

  // Browser-level actions (reload, close tab) always require confirmation
  // while this is the topmost completed-order modal.
  useEffect(() => {
    if (!navigationGuardActive) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [navigationGuardActive]);

  // Prevent a horizontal two-finger trackpad gesture from becoming browser
  // Back/Forward navigation. Normal vertical and in-modal scrolling still work.
  useEffect(() => {
    if (!navigationGuardActive) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const previousRootValue = root.style.overscrollBehaviorX;
    const previousBodyValue = body.style.overscrollBehaviorX;
    root.style.overscrollBehaviorX = "none";
    body.style.overscrollBehaviorX = "none";
    return () => {
      root.style.overscrollBehaviorX = previousRootValue;
      body.style.overscrollBehaviorX = previousBodyValue;
    };
  }, [navigationGuardActive]);

  // Explicit browser Back/Forward navigation is allowed after confirmation.
  useEffect(() => {
    if (!navigationGuardActive) return undefined;
    window.history.pushState({ modalOpen: true }, "");
    let confirmationOpen = false;
    let restoringGuardEntry = false;
    const handlePopState = () => {
      if (restoringGuardEntry) {
        restoringGuardEntry = false;
        return;
      }
      if (confirmationOpen) return;
      confirmationOpen = true;
      Modal.confirm({
        title: "Leave completed order creation?",
        content: "Your current progress will be cleared if you leave this page.",
        okText: "Leave",
        okButtonProps: { danger: true },
        cancelText: "Stay",
        onOk: () => {
          window.removeEventListener("popstate", handlePopState);
          closeAndReset();
          window.history.back();
        },
        onCancel: () => {
          confirmationOpen = false;
          restoringGuardEntry = true;
          window.history.forward();
        },
      });
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigationGuardActive]);


  const handleSubmit = async () => {
    // Recheck all required fields after revisiting earlier steps.
    for (let stepIndex = STEP.CLIENT_PROJECT; stepIndex < STEP.REVIEW; stepIndex++) {
      const missing = getMissingFields(stepIndex);
      if (missing.length) {
        setAttemptedSteps((prev) => new Set(prev).add(stepIndex));
        setStep(stepIndex);
        message.warning(`Please fill in: ${missing.map(([, label]) => label).join(", ")}`);
        return;
      }
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      message.warning("Enter a valid Notification Email address");
      return;
    }
    if (!isValidQuantity(data.quantity)) {
      setAttemptedSteps((prev) => new Set(prev).add(STEP.ORDER_DETAILS));
      setStep(STEP.ORDER_DETAILS);
      message.warning("Quantity must be a number greater than or equal to 0");
      return;
    }
    if (!isValidQuantity(data.sampleWeight) || !isValidQuantity(data.sampleCost)) {
      setAttemptedSteps((prev) => new Set(prev).add(STEP.SAMPLE_PURCHASE));
      setStep(STEP.SAMPLE_PURCHASE);
      message.warning(!isValidQuantity(data.sampleWeight)
        ? "Sample Weight must be a number greater than or equal to 0"
        : "Sample Cost must be a number greater than or equal to 0");
      return;
    }
    const dateErrors = validateTimeline(data);
    if (dateErrors.length) {
      setAttemptedSteps((prev) => new Set(prev).add(STEP.DATES));
      setStep(STEP.DATES);
      message.warning(dateErrors[0][1]);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await createHistoricalOrder(buildHistoricalOrderFormData({ ...normalizeDatesForSubmit(data), notificationEmail: email.trim() }));
      const result = response?.data?.data;
      setCreatedOrder(result);
      message.success(`Order ${result?.orderId} created successfully.`);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Failed to create order";
      setSubmitError(msg);
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Real reference lists, cascade-filtered exactly like the Historical
  // Orders sheet: Project by Company, Commodity by Project, Package by
  // Company + Commodity + Sample Variant. Labs/Logistics are flat.
  const companies = masterData?.companies || [];
  const projectsForCompany = (masterData?.projects || []).filter((p) => p.company_id === data.company);
  const commoditiesForProject = (masterData?.commodities || []).filter((c) => c.project_id === data.projectCode);
  const packagesForSelection = (masterData?.packages || []).filter((p) =>
    p.company_id === data.company && p.commodity_id === data.commodity && (!data.sampleVariant || p.variant === data.sampleVariant));
  const labs = masterData?.labs || [];
  const logistics = masterData?.logistics || [];
  // Flat, like Labs/Logistics: the real Agency documents in this dataset all
  // have associatedCompany (company_id) unset, so filtering by company would
  // always produce an empty list — verified against the real DB, not assumed.
  const agenciesForCompany = masterData?.agencies || [];
  // Same State -> District cascade as the live "New Order" flow (State
  // dropdown, District dropdown disabled/reset until a State is picked) —
  // stored as plain names, not ids, since that's what historicalOrderCreate
  // .service.js's stateName/districtName resolve against (see its header
  // comment: state/district are still resolved by name, matching the
  // proven import script, because the real Order schema stores them as
  // plain strings either way).
  const states = masterData?.states || [];
  const selectedState = states.find((s) => s.name === data.state);
  const districtsForState = (masterData?.districts || []).filter((d) => d.state_id === selectedState?.id);

  const nameFor = (list, id, key = "name") => list.find((item) => item.id === id)?.[key];

  const isBlank = (value) => value === null || value === undefined || value === "";
  const getMissingFields = (stepIndex) => {
    const missing = (REQUIRED_FIELDS_BY_STEP[stepIndex] || []).filter(([key]) => isBlank(data[key]));
    if (stepIndex === STEP.DATES && data.targetEndStatus === TARGET_END_STATUS.COMPLETED && isBlank(data.completedDate)) {
      missing.push(["completedDate", "Completed Date"]);
    }
    return missing;
  };
  // Only shows a field's red outline once its step has actually been
  // attempted — before that, the asterisk alone is the indicator.
  const showError = (stepIndex, field) => attemptedSteps.has(stepIndex) && [...getMissingFields(stepIndex), ...(stepIndex === STEP.DATES ? validateTimeline(data) : [])].some(([key]) => key === field);

  const goToNextStep = (currentStep, nextStep) => {
    const missing = getMissingFields(currentStep);
    if (missing.length) {
      setAttemptedSteps((prev) => new Set(prev).add(currentStep));
      message.warning(`Please fill in: ${missing.map(([, label]) => label).join(", ")}`);
      return;
    }
    if (currentStep === STEP.ORDER_DETAILS && !isValidQuantity(data.quantity)) {
      setAttemptedSteps((prev) => new Set(prev).add(STEP.ORDER_DETAILS));
      message.warning("Quantity must be a number greater than or equal to 0");
      return;
    }
    if (currentStep === STEP.SAMPLE_PURCHASE && (!isValidQuantity(data.sampleWeight) || !isValidQuantity(data.sampleCost))) {
      setAttemptedSteps((prev) => new Set(prev).add(STEP.SAMPLE_PURCHASE));
      message.warning(!isValidQuantity(data.sampleWeight)
        ? "Sample Weight must be a number greater than or equal to 0"
        : "Sample Cost must be a number greater than or equal to 0");
      return;
    }
    if (currentStep === STEP.DATES) {
      const errors = validateTimeline(data);
      if (errors.length) {
        setAttemptedSteps((prev) => new Set(prev).add(STEP.DATES));
        message.warning(errors[0][1]);
        return;
      }
    }
    setFurthestStep((previous) => Math.max(previous, nextStep));
    setStep(nextStep);
  };

  // A single persistent footer (Back/Next/Submit/Close), computed off the
  // current step, so those actions are always visible at the bottom of the
  // modal — the step content beneath it scrolls in its own bounded area
  // instead of pushing them below the fold.
  const NEXT_STEP_BY_STEP = {
    [STEP.ORDER_DETAILS]: STEP.SAMPLE_PURCHASE,
    [STEP.SAMPLE_PURCHASE]: STEP.LAB_LOGISTICS,
    [STEP.LAB_LOGISTICS]: STEP.DATES,
    [STEP.DATES]: STEP.REPORT_COMPLIANCE,
    [STEP.REPORT_COMPLIANCE]: STEP.REVIEW,
  };

  let footerButtons;
  if (step === STEP.REVIEW && createdOrder) {
    footerButtons = <Button type="primary" onClick={closeAndReset}>Close</Button>;
  } else if (step === STEP.REVIEW) {
    footerButtons = (
      <>
        <Button onClick={() => setStep(STEP.REPORT_COMPLIANCE)} style={{ marginRight: 12 }} disabled={submitting}>Back</Button>
        <Button type="primary" onClick={handleSubmit} loading={submitting}>
          {submitting ? "Creating order…" : "Submit"}
        </Button>
      </>
    );
  } else if (step === STEP.CLIENT_PROJECT) {
    footerButtons = (
      <>
        <Button onClick={confirmCloseIfNeeded} style={{ marginRight: 12 }}>Cancel</Button>
        <Button type="primary" onClick={() => goToNextStep(STEP.CLIENT_PROJECT, STEP.ORDER_DETAILS)}>Next</Button>
      </>
    );
  } else {
    footerButtons = (
      <>
        <Button onClick={() => setStep(step - 1)} style={{ marginRight: 12 }}>Back</Button>
        <Button type="primary" onClick={() => goToNextStep(step, NEXT_STEP_BY_STEP[step])}>Next</Button>
      </>
    );
  }

  const summaryRows = [
    { label: "Company", value: nameFor(companies, data.company) },
    { label: "Project Code", value: nameFor(masterData?.projects || [], data.projectCode, "code") },
    { label: "Commodity", value: nameFor(masterData?.commodities || [], data.commodity) },
    { label: "Package", value: nameFor(masterData?.packages || [], data.package) },
    { label: "Target End Status", value: data.targetEndStatus },
    { label: "Report File", value: data.reportFile?.name },
    { label: "Metadata File", value: data.reportMetadataFile?.name },
    ...SAMPLE_PHOTO_FIELDS.map((field) => ({ label: FIELD_DEF_BY_FIELD[field].label, value: data[field]?.name })),
  ].filter((r) => r.value);

  return (
    <Modal
      visible={visible}
      onCancel={confirmCloseIfNeeded}
      maskClosable={false}
      keyboard={!hasUnsavedData}
      title="Add Completed Order"
      width="90vw"
      style={{ maxWidth: 1200, top: 24 }}
      footer={masterDataLoading ? null : <div style={{ textAlign: "right" }}>{footerButtons}</div>}
      destroyOnClose
    >
      {/* Long step titles ("Sample & Purchase", "Report & Compliance") don't fit
          antd's default single-line, ellipsis-truncated Steps title within a
          reasonable modal width — wrapping them onto two lines keeps the full
          label readable instead of cutting it off mid-word. */}
      <style>{`
        .aco-steps .ant-steps-item-title { white-space: normal; line-height: 1.3; }
      `}
      </style>

      {masterDataError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not load company/project reference data"
          description={masterDataError}
          action={<Button size="small" onClick={refreshMasterData}>Retry</Button>}
        />
      )}

      {masterDataLoading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin tip="Loading company, project and reference data…" />
        </div>
      ) : (
        <>
          <Steps size="small" current={step} className="aco-steps" style={{ marginBottom: 20 }}
            onChange={(targetStep) => {
              if (!submitting && !createdOrder && targetStep >= STEP.CLIENT_PROJECT && targetStep <= furthestStep) {
                setStep(targetStep);
              }
            }}
          >
            <Step title="Client & Project" disabled={submitting || !!createdOrder || STEP.CLIENT_PROJECT > furthestStep} />
            <Step title="Order Details" disabled={submitting || !!createdOrder || STEP.ORDER_DETAILS > furthestStep} />
            <Step title="Sample & Purchase" disabled={submitting || !!createdOrder || STEP.SAMPLE_PURCHASE > furthestStep} />
            <Step title="Lab & Logistics" disabled={submitting || !!createdOrder || STEP.LAB_LOGISTICS > furthestStep} />
            <Step title="Dates & Status" disabled={submitting || !!createdOrder || STEP.DATES > furthestStep} />
            <Step title="Report & Compliance" disabled={submitting || !!createdOrder || STEP.REPORT_COMPLIANCE > furthestStep} />
            <Step title="Review & Submit" disabled={submitting || !!createdOrder || STEP.REVIEW > furthestStep} />
          </Steps>

          <div style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto", paddingRight: 8 }}>

          {step === STEP.CLIENT_PROJECT && (
            <>
              <Text type="secondary">Fields marked <Text type="danger">*</Text> are required — everything else is optional.</Text>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Field label="Company" required>
                  <FieldSelect
                    value={data.company}
                    options={companies}
                    onChange={(v) => updateWithReset("company", v, ["projectCode", "commodity", "package"])}
                    hasError={showError(STEP.CLIENT_PROJECT, "company")}
                    placeholder="Select Company"
                  />
                </Field>
                <Field label="Project Code" required>
                  <FieldSelect
                    value={data.projectCode}
                    options={projectsForCompany.map((p) => ({ id: p.id, name: p.code }))}
                    onChange={(v) => updateWithReset("projectCode", v, ["commodity", "package"])}
                    disabled={!data.company}
                    hasError={showError(STEP.CLIENT_PROJECT, "projectCode")}
                    placeholder="Select Project Code"
                  />
                </Field>
              </Row>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
                <div>
                  <Text strong style={{ display: "block" }}>Onboarding many completed orders at once?</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>Use the sheet-based bulk flow instead of filling this form one at a time.</Text>
                </div>
                <HistoricalOrderBulkUpload
                  triggerLabel="Bulk Completed Order Creation"
                  onVisibilityChange={setBulkModalVisible}
                />
              </div>
            </>
          )}

          {step === STEP.ORDER_DETAILS && (
            <>
              <Row gutter={16}>
                <Field label="Agency" required>
                  <FieldSelect
                    value={data.agency}
                    options={agenciesForCompany}
                    onChange={(v) => update("agency", v)}
                    hasError={showError(STEP.ORDER_DETAILS, "agency")}
                    placeholder="Select Agency"
                  />
                </Field>
                <Field label="Commodity" required>
                  <FieldSelect
                    value={data.commodity}
                    options={commoditiesForProject}
                    onChange={(v) => updateWithReset("commodity", v, ["package"])}
                    disabled={!data.projectCode}
                    hasError={showError(STEP.ORDER_DETAILS, "commodity")}
                    placeholder="Select Commodity"
                  />
                </Field>
                <Field label="Sample Variant" required>
                  <FieldSelect
                    value={data.sampleVariant}
                    options={FIXED_OPTIONS.sampleVariant}
                    onChange={(v) => updateWithReset("sampleVariant", v, ["package"])}
                    hasError={showError(STEP.ORDER_DETAILS, "sampleVariant")}
                    placeholder="Select Sample Variant"
                  />
                </Field>
                <Field label="Package" required>
                  <FieldSelect
                    value={data.package}
                    options={packagesForSelection}
                    onChange={(v) => update("package", v)}
                    disabled={!data.commodity}
                    hasError={showError(STEP.ORDER_DETAILS, "package")}
                    placeholder="Select Package"
                  />
                </Field>
                <Field label="Sample Type" required><FieldSelect value={data.sampleType} options={FIXED_OPTIONS.sampleType} onChange={(v) => update("sampleType", v)} hasError={showError(STEP.ORDER_DETAILS, "sampleType")} placeholder="Select Sample Type" /></Field>
                <Field label="Sector" required><FieldSelect value={data.sector} options={FIXED_OPTIONS.sector} onChange={(v) => update("sector", v)} hasError={showError(STEP.ORDER_DETAILS, "sector")} placeholder="Select Sector" /></Field>
                <Field label="State" required>
                  <FieldSelect
                    value={data.state}
                    options={states.map((s) => s.name)}
                    onChange={(v) => updateWithReset("state", v, ["district"])}
                    hasError={showError(STEP.ORDER_DETAILS, "state")}
                    placeholder="Select State"
                  />
                </Field>
                <Field label="District" required>
                  <FieldSelect
                    value={data.district}
                    options={districtsForState.map((d) => d.name)}
                    onChange={(v) => update("district", v)}
                    disabled={!data.state}
                    hasError={showError(STEP.ORDER_DETAILS, "district")}
                    placeholder="Select District"
                  />
                </Field>
                <Field label="Sampler"><Input value={data.sampler} onChange={(e) => update("sampler", e.target.value)} placeholder="Enter Sampler" /></Field>
                <Field label="Site Address"><Input value={data.siteAddress} onChange={(e) => update("siteAddress", e.target.value)} placeholder="Enter Site Address" /></Field>
                <Field label="Batch Id"><Input value={data.batchId} onChange={(e) => update("batchId", e.target.value)} placeholder="Enter Batch Id" /></Field>
                <Field label="Supplier" required><Input value={data.supplier} onChange={(e) => update("supplier", e.target.value)} placeholder="Enter Supplier" style={errorStyle(showError(STEP.ORDER_DETAILS, "supplier"))} /></Field>
                <Field label="Quantity" required>
                  <Input type="number" min={0} step="any" value={data.quantity} onChange={(e) => update("quantity", e.target.value)} placeholder="Enter Quantity" style={errorStyle(showError(STEP.ORDER_DETAILS, "quantity") || (data.quantity !== "" && !isValidQuantity(data.quantity)))} />
                  {data.quantity !== "" && !isValidQuantity(data.quantity) && <Text type="danger">Quantity must be a number greater than or equal to 0</Text>}
                </Field>
                <Field label="Quantity Unit" required><FieldSelect value={data.quantityUnit} options={FIXED_OPTIONS.quantityUnit} onChange={(v) => update("quantityUnit", v)} hasError={showError(STEP.ORDER_DETAILS, "quantityUnit")} placeholder="Select Quantity Unit" /></Field>
                <Field label="Notes"><Input value={data.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Enter Notes" /></Field>
              </Row>
            </>
          )}

          {step === STEP.SAMPLE_PURCHASE && (
            <>
              <Row gutter={16}>
                <Field label="Name of Shop" required><Input value={data.nameOfShop} onChange={(e) => update("nameOfShop", e.target.value)} style={errorStyle(showError(STEP.SAMPLE_PURCHASE, "nameOfShop"))} placeholder="Enter Name of Shop" /></Field>
                <Field label="Address of Shop" required><Input value={data.addressOfShop} onChange={(e) => update("addressOfShop", e.target.value)} style={errorStyle(showError(STEP.SAMPLE_PURCHASE, "addressOfShop"))} placeholder="Enter Address of Shop" /></Field>
                <Field label="Sample Weight (g)" required>
                  <Input type="number" min={0} step="any" value={data.sampleWeight} onChange={(e) => update("sampleWeight", e.target.value)} placeholder="Enter Sample Weight" style={errorStyle(showError(STEP.SAMPLE_PURCHASE, "sampleWeight") || (data.sampleWeight !== "" && !isValidQuantity(data.sampleWeight)))} />
                  {data.sampleWeight !== "" && !isValidQuantity(data.sampleWeight) && <Text type="danger">Sample Weight must be a number greater than or equal to 0</Text>}
                </Field>
                <Field label="Sample Cost (INR)" required>
                  <Input type="number" min={0} step="any" value={data.sampleCost} onChange={(e) => update("sampleCost", e.target.value)} placeholder="Enter Sample Cost" style={errorStyle(showError(STEP.SAMPLE_PURCHASE, "sampleCost") || (data.sampleCost !== "" && !isValidQuantity(data.sampleCost)))} />
                  {data.sampleCost !== "" && !isValidQuantity(data.sampleCost) && <Text type="danger">Sample Cost must be a number greater than or equal to 0</Text>}
                </Field>
                <Field label="Type of Shop" required><FieldSelect value={data.typeOfShop} options={FIXED_OPTIONS.typeOfShop} onChange={(v) => update("typeOfShop", v)} hasError={showError(STEP.SAMPLE_PURCHASE, "typeOfShop")} placeholder="Select Type of Shop" /></Field>
                <Field label="Sample Seal No"><Input value={data.sampleSealNo} onChange={(e) => update("sampleSealNo", e.target.value)} placeholder="Enter Sample Seal No (auto-generated if left blank)" /></Field>
                <Field label="QR Code Id"><Input value={data.qrCodeId} onChange={(e) => update("qrCodeId", e.target.value)} placeholder="Enter QR Code Id (auto-generated if left blank)" /></Field>
                <Field label="Sample Packed In" required><FieldSelect value={data.samplePackedIn} options={FIXED_OPTIONS.samplePackedIn} onChange={(v) => update("samplePackedIn", v)} hasError={showError(STEP.SAMPLE_PURCHASE, "samplePackedIn")} placeholder="Select Sample Packed In" /></Field>
                <Field label="FSSAI Logo Present?" required><FieldSelect value={data.fssaiLogoPresent} options={FIXED_OPTIONS.yesNo} onChange={(v) => update("fssaiLogoPresent", v)} hasError={showError(STEP.SAMPLE_PURCHASE, "fssaiLogoPresent")} placeholder="Select FSSAI Logo Present?" /></Field>
                <Field label="Veg Symbol Present?" required><FieldSelect value={data.vegSymbolPresent} options={FIXED_OPTIONS.yesNo} onChange={(v) => update("vegSymbolPresent", v)} hasError={showError(STEP.SAMPLE_PURCHASE, "vegSymbolPresent")} placeholder="Select Veg Symbol Present?" /></Field>
                <Field label="Sample Physical Condition" required><FieldSelect value={data.samplePhysicalCondition} options={FIXED_OPTIONS.samplePhysicalCondition} onChange={(v) => update("samplePhysicalCondition", v)} hasError={showError(STEP.SAMPLE_PURCHASE, "samplePhysicalCondition")} placeholder="Select Sample Physical Condition" /></Field>
                <Field label="Condition of Seal" required><FieldSelect value={data.conditionOfSeal} options={FIXED_OPTIONS.conditionOfSeal} onChange={(v) => update("conditionOfSeal", v)} hasError={showError(STEP.SAMPLE_PURCHASE, "conditionOfSeal")} placeholder="Select Condition of Seal" /></Field>
                <Field label="Sample Registration Number"><Input value={data.sampleRegistrationNumber} onChange={(e) => update("sampleRegistrationNumber", e.target.value)} placeholder="Enter Sample Registration Number (auto-generated if left blank)" /></Field>
              </Row>
            </>
          )}

          {step === STEP.LAB_LOGISTICS && (
            <>
              <Row gutter={16}>
                <Field label="Assigned Lab" required><FieldSelect value={data.assignedLab} options={labs} onChange={(v) => update("assignedLab", v)} hasError={showError(STEP.LAB_LOGISTICS, "assignedLab")} placeholder="Select Assigned Lab" /></Field>
                <Field label="Assigned Logistics" required><FieldSelect value={data.assignedLogistics} options={logistics} onChange={(v) => update("assignedLogistics", v)} hasError={showError(STEP.LAB_LOGISTICS, "assignedLogistics")} placeholder="Select Assigned Logistics" /></Field>
                <Field label="Tracking Id"><Input value={data.trackingId} onChange={(e) => update("trackingId", e.target.value)} placeholder="Enter Tracking Id" /></Field>
              </Row>
            </>
          )}

          {step === STEP.DATES && (
            <>
              <Text type="secondary">
                Select one date for each group to apply it to all three stages. Dates must stay
                in chronological order; same-day stages are allowed and future dates are blocked.
                Expected Completion can be before or after Report Uploaded, but cannot precede testing.
                Every date is required except Completed Date, which only applies (and is only
                required) when Target End Status is "Completed".
              </Text>
              <div style={{ marginTop: 12 }}>
                <Checkbox checked={separateStageDates} onChange={(e) => {
                  const separate = e.target.checked;
                  setSeparateStageDates(separate);
                  if (!separate) setData((prev) => {
                    const next = { ...prev };
                    DATE_GROUPS.forEach((keys) => keys.forEach((key) => { next[key] = prev[keys[0]]; }));
                    return next;
                  });
                }}>Use separate stage dates</Checkbox>
              </div>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Field label="Date of Purchase" required>
                  <FieldDate
                    value={data.dateOfPurchase}
                    onChange={(v) => update("dateOfPurchase", v)}
                    disabledDate={chainDisabledDate(data, "dateOfPurchase")}
                    hasError={showError(STEP.DATES, "dateOfPurchase")}
                    placeholder="Select Date of Purchase"
                  />
                </Field>
                {(separateStageDates ? STAGE_DATE_FIELDS.slice(0, 8) : [
                  ["pendingDate", "Pending / Booked / In Review Date"],
                  ["readyForPickupDate", "Ready for Pickup / Out for Pickup / Delivered to Lab Date"],
                  ...STAGE_DATE_FIELDS.slice(6, 8),
                ]).map(([key, label]) => (
                  <Field key={key} label={label} required>
                    <FieldDate
                      value={data[key]}
                      onChange={(v) => update(key, v)}
                      disabledDate={chainDisabledDate(data, key, !separateStageDates)}
                      hasError={showError(STEP.DATES, key)}
                      placeholder={`Select ${label}`}
                    />
                  </Field>
                ))}
                <Field label="Expected Date of Completion" required>
                  <FieldDate
                    value={data.expectedDateOfCompletion}
                    onChange={(v) => update("expectedDateOfCompletion", v)}
                    disabledDate={expectedCompletionDisabledDate(data)}
                    hasError={showError(STEP.DATES, "expectedDateOfCompletion")}
                    placeholder="Select Expected Date of Completion"
                  />
                </Field>
                {STAGE_DATE_FIELDS.slice(8).map(([key, label]) => (
                  <Field key={key} label={label} required>
                    <FieldDate
                      value={data[key]}
                      onChange={(v) => update(key, v)}
                      disabledDate={chainDisabledDate(data, key, !separateStageDates)}
                      hasError={showError(STEP.DATES, key)}
                      placeholder={`Select ${label}`}
                    />
                  </Field>
                ))}
              </Row>
              <Row gutter={16}>
                <Field label="Target End Status" required>
                  <FieldSelect
                    value={data.targetEndStatus}
                    options={Object.values(TARGET_END_STATUS)}
                    onChange={(v) => {
                      update("targetEndStatus", v);
                      if (v === TARGET_END_STATUS.REPORT_UPLOADED) update("completedDate", null);
                    }}
                    hasError={showError(STEP.DATES, "targetEndStatus")}
                    placeholder="Select Target End Status"
                  />
                </Field>
                <Field label="Completed Date" required={data.targetEndStatus === TARGET_END_STATUS.COMPLETED}>
                  <FieldDate
                    value={data.completedDate}
                    onChange={(v) => update("completedDate", v)}
                    disabledDate={chainDisabledDate(data, "completedDate")}
                    disabled={data.targetEndStatus !== TARGET_END_STATUS.COMPLETED}
                    hasError={showError(STEP.DATES, "completedDate")}
                    placeholder="Select Completed Date"
                  />
                </Field>
              </Row>
            </>
          )}

          {step === STEP.REPORT_COMPLIANCE && (
            <>
              <Row gutter={16}>
                <Field label="Report File" required><FieldFileUpload file={data.reportFile} onSelect={(file) => update("reportFile", file)} hasError={showError(STEP.REPORT_COMPLIANCE, "reportFile")} /></Field>
                <Field label="Report Metadata File" required><FieldFileUpload file={data.reportMetadataFile} onSelect={(file) => update("reportMetadataFile", file)} hasError={showError(STEP.REPORT_COMPLIANCE, "reportMetadataFile")} /></Field>
                <Field label="Overall Safety Compliant" required><FieldSelect value={data.overallSafetyCompliant} options={FIXED_OPTIONS.overallSafetyCompliant} onChange={(v) => update("overallSafetyCompliant", v)} hasError={showError(STEP.REPORT_COMPLIANCE, "overallSafetyCompliant")} placeholder="Select Overall Safety Compliant" /></Field>
                <Field label="Overall Quality Compliant" required><FieldSelect value={data.overallQualityCompliant} options={FIXED_OPTIONS.overallQualityCompliant} onChange={(v) => update("overallQualityCompliant", v)} hasError={showError(STEP.REPORT_COMPLIANCE, "overallQualityCompliant")} placeholder="Select Overall Quality Compliant" /></Field>
                <Field label="Overall Labelling Compliant" required><FieldSelect value={data.overallLabellingCompliant} options={FIXED_OPTIONS.overallLabellingCompliant} onChange={(v) => update("overallLabellingCompliant", v)} hasError={showError(STEP.REPORT_COMPLIANCE, "overallLabellingCompliant")} placeholder="Select Overall Labelling Compliant" /></Field>
                <Field label="Overall Sample Classification" required><FieldSelect value={data.overallSampleClassification} options={FIXED_OPTIONS.overallSampleClassification} onChange={(v) => update("overallSampleClassification", v)} hasError={showError(STEP.REPORT_COMPLIANCE, "overallSampleClassification")} placeholder="Select Overall Sample Classification" /></Field>
              </Row>

              <Title level={5} style={{ marginTop: 8 }}>Optional Sample Photos</Title>
              <Text type="secondary">
                Same optional photos the bulk sheet flow accepts (QR code, FSSAI logo, packaging, etc.) —
                an order with none of these attached is exactly as valid as one with all of them.
              </Text>
              <Row gutter={16} style={{ marginTop: 16 }}>
                {SAMPLE_PHOTO_FIELDS.map((field) => (
                  <Field key={field} label={FIELD_DEF_BY_FIELD[field].label}>
                    <FieldFileUpload file={data[field]} onSelect={(file) => update(field, file)} />
                  </Field>
                ))}
              </Row>
            </>
          )}

          {step === STEP.REVIEW && (
            <>
              {!createdOrder ? (
                <>
                  <Title level={5}>Summary</Title>
                  {summaryRows.length ? (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={summaryRows}
                      rowKey="label"
                      columns={[
                        { title: "Field", dataIndex: "label", width: 200 },
                        { title: "Value", dataIndex: "value" },
                      ]}
                    />
                  ) : (
                    <Text type="secondary">No fields filled in yet.</Text>
                  )}

                  <div style={{ marginTop: 20, maxWidth: 360 }}>
                    <Text strong>Notification Email</Text>
                    <Input
                      type="email"
                      placeholder="Enter Notification Email (optional)"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ marginTop: 6 }}
                    />
                  </div>

                  {submitError && (
                    <Alert type="error" showIcon style={{ marginTop: 20 }} message="Could not create the order" description={submitError} />
                  )}
                </>
              ) : (
                <>
                  <CheckCircleTwoTone twoToneColor="#52C41A" style={{ fontSize: 32 }} />
                  <Title level={5} style={{ marginTop: 12 }}>Order created</Title>
                  {createdOrder.notification?.status === "accepted" && (
                    <Alert type="success" showIcon style={{ marginBottom: 12 }} message="Notification email accepted by the mail server." />
                  )}
                  {createdOrder.notification?.status === "failed" && (
                    <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Order created, but the notification email could not be sent. Do not resubmit the order." />
                  )}
                  <Text>
                    <Text strong>{createdOrder.orderId}</Text> was created and landed at{" "}
                    <Text strong>{data.targetEndStatus}</Text>. Report/metadata files were uploaded and parsing was triggered.
                  </Text>
                </>
              )}
            </>
          )}

          </div>
        </>
      )}
    </Modal>
  );
}
